import { BrandMark } from "@/components/brand";
import { Menu, Mic, X, Zap } from "lucide-react";

const levelLabels = {
  absolute_beginner: "First steps",
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
} as const;

interface ChatHeaderProps {
  languageName: string;
  languageLevel: "absolute_beginner" | "beginner" | "intermediate" | "advanced";
  onClose?: () => void;
  onToggleSidebar?: () => void;
  onGenerateWidget?: () => void;
  isWidgetLoading?: boolean;
  onContinueSession?: () => void;
}

export function ChatHeader({
  languageName,
  languageLevel,
  onClose,
  onToggleSidebar,
  onGenerateWidget,
  isWidgetLoading,
  onContinueSession,
}: ChatHeaderProps) {
  return (
    <header className="flex min-h-16 items-center justify-between gap-3 border-b border-white/10 bg-[#0f0f14] px-3 py-3 sm:px-5">
      <div className="flex min-w-0 items-center gap-3">
        {onToggleSidebar && (
          <button
            type="button"
            onClick={onToggleSidebar}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-white/10 text-white/55 transition-colors hover:bg-white/[0.06] hover:text-white"
            title="Past conversations"
            aria-label="Open past conversations"
          >
            <Menu size={17} />
          </button>
        )}
        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/10 bg-white/[0.04]">
          <BrandMark size={28} className="h-7 w-7 object-contain" alt="Chicky AI Tutor" />
        </div>
        <div className="min-w-0">
          <h1 className="truncate text-sm font-bold text-white sm:text-base">Writing practice</h1>
          <p className="truncate text-xs text-white/45">
            {languageName} / {levelLabels[languageLevel]}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {onContinueSession ? (
          <button
            type="button"
            onClick={onContinueSession}
            className="flex h-9 items-center gap-2 rounded-md border border-orange-300/30 bg-orange-400/10 px-2.5 text-xs font-semibold text-orange-200 transition-colors hover:border-orange-300/45 hover:bg-orange-400/15 hover:text-orange-100 sm:px-3"
            title="Continue this session"
            aria-label="Continue this session"
          >
            <Mic size={14} />
            <span className="hidden sm:inline">Continue this session</span>
          </button>
        ) : onGenerateWidget ? (
          <button
            type="button"
            onClick={onGenerateWidget}
            disabled={isWidgetLoading}
            className="flex h-9 items-center gap-2 rounded-md border border-white/10 px-2.5 text-xs font-semibold text-white/60 transition-colors hover:border-orange-300/30 hover:bg-white/[0.05] hover:text-white disabled:cursor-not-allowed disabled:opacity-50 sm:px-3"
            title="Generate an exercise"
            aria-label={isWidgetLoading ? "Generating exercise" : "Generate an exercise"}
          >
            <Zap size={14} className={isWidgetLoading ? "animate-pulse text-orange-300" : ""} />
            <span className="hidden sm:inline">{isWidgetLoading ? "Generating" : "Exercise"}</span>
          </button>
        ) : null}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-white/55 transition-colors hover:bg-white/[0.06] hover:text-white"
            title="Close writing practice"
            aria-label="Close writing practice"
          >
            <X size={17} />
          </button>
        )}
      </div>
    </header>
  );
}
